import React, { useState } from 'react'

import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

import { Option } from 'components_new/molecules/SelectSearch'

import { useTranslation } from 'language/translator'
import { useWidgetsInDashboard } from 'redux/hooks/DashboardWidgets'
import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'
import { TDashboard } from 'types/GlobalDashboard'

interface PreviewWidgetsProps {
  dashboard: TDashboard
}

const PreviewWidgets = (props: PreviewWidgetsProps) => {
  const { dashboard } = props

  const { t } = useTranslation()
  const dashboardWidgets = useWidgetsInDashboard(dashboard.id)
  const { tryUpdateDashboardWidget } = useDashboardWidgetActions()

  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedWidgetId, setSelectedWidgetId] = useState('')

  const previewWidgets = dashboardWidgets.filter((w) => w.settings?.is_preview)

  const availableWidgets: Option[] = dashboardWidgets
    .filter((w) => !w.settings?.is_preview && w.settings?.allow_preview)
    .map((w) => ({
      title: w.title,
      value: w.id,
      id: w.id
    }))

  const handleAdd = () => {
    if (selectedWidgetId) {
      tryUpdateDashboardWidget(selectedWidgetId, {
        data: { is_preview: true }
      })
    }

    setAddDialogOpen(false)
    setSelectedWidgetId('')
  }

  const handleRemove = (widgetId: string) => {
    tryUpdateDashboardWidget(widgetId, { data: { is_preview: false } })
  }

  return (
    <>
      <SettingsGroup
        helperText={t('dashboards', 'include_widgets_helper')}
        title={t('dashboards', 'include_widgets')}
      >
        {previewWidgets.map((widget) => (
          <SettingsItem
            key={widget.id}
            actions={
              <Tooltip title={t('global', 'delete')}>
                <IconButton
                  onClick={() => handleRemove(widget.id)}
                  size="small"
                >
                  <Icon name="Delete" fontSize="small" />
                </IconButton>
              </Tooltip>
            }
            title={widget.title}
          />
        ))}
        <SettingsItem
          disabled={availableWidgets.length === 0}
          disabledText={t('dashboards', 'disabled_preview_widget_tooltip')}
          onClick={() => setAddDialogOpen(true)}
          title={t('widgets', 'add_widget')}
          variant="add"
        />
      </SettingsGroup>

      <SettingsEditDialog
        onChange={(value: string) => setSelectedWidgetId(value)}
        onClose={() => {
          setAddDialogOpen(false)
          setSelectedWidgetId('')
        }}
        onSubmit={handleAdd}
        open={addDialogOpen}
        options={availableWidgets}
        title={t('widgets', 'add_widget')}
        type="select"
        value={selectedWidgetId}
        variant="add"
      />
    </>
  )
}

export default PreviewWidgets
